import { ref, watch } from 'vue';
import { useLens } from '@/composables/api/lens/useLens';
import router from '@/router';
import { lensPanelPage, pageTitle } from '@/components/lens/lensPanelPages';
import { $http } from '@/common/api/http.service';
import { useAuthStore } from '@/stores/auth';

const CAPABILITY_ID = 'lens.chat';
// At most this many of the page's guides are offered as chips when the panel opens.
const PAGE_GUIDE_CHIPS = 3;

// Module-level: the header icon and the panel share one conversation, and it outlives navigation.
const open = ref(false);
const messages = ref([]);
const isStreaming = ref(false);
// The help guides offered for the page the panel was opened on (#6467): up to PAGE_GUIDE_CHIPS of the
// page's guides, picked at random on each open. Every page's full list is kept for the session.
const pageGuides = ref([]);
const guidesByRoute = new Map();

let lens = null;
// The account the panel's state belongs to; a switch to another account starts the panel afresh.
let accountWatched = false;
// The assistant message the current turn streams into.
let current = null;
// Bumped by every send and by stop/startOver, so a superseded turn's late finish changes nothing.
let turn = 0;

function errorOf(event) {
    return { message: event?.error?.message ?? 'Something went wrong.', code: event?.error?.code ?? 'error' };
}

function onEvent(event) {
    if (!current) return;
    switch (event?.type) {
        case 'text':
            current.content = lens.text.value || current.content + (event.delta ?? '');
            break;
        case 'result':
            // By kind: guides (server-built links) or pageLink (#6467).
            current.result = event.result ?? null;
            break;
        case 'error':
            current.error = errorOf(event);
            isStreaming.value = false;
            break;
        case 'route':
            // The capability the panel router handed the turn to; done confirms it.
            current.capability = event.capability ?? current.capability;
            break;
        case 'done':
            // Kept for the Help answer rating and guide click tracking (#6535).
            current.turnId = event.turnId || null;
            current.capability = event.capability ?? current.capability;
            current.outcome = event.outcome ?? null;
            isStreaming.value = false;
            break;
        default:
            break;
    }
}

function ensureLens() {
    if (lens) return lens;
    lens = useLens(CAPABILITY_ID, { onEvent });
    watch(lens.text, (text) => {
        if (current && text) current.content = text;
    });
    // A refusal before streaming started (PHI screen, rate limit, network) sets error without an event.
    watch(lens.error, (error) => {
        if (current && error) {
            current.error = { message: error.message, code: error.code };
            isStreaming.value = false;
        }
    });
    return lens;
}

/**
 * Where the user is: the route name and the page's title, never params, query or URL, and the
 * capability that owns the panel there, if one does.
 */
function currentPage() {
    const route = router.currentRoute?.value ?? {};
    const { capability } = lensPanelPage(route);
    return { routeName: String(route.name ?? ''), label: pageTitle(route), ...(capability ? { capability } : {}) };
}

/** Sends a message; the panel router decides which capability answers it. */
async function send(text) {
    const message = String(text ?? '').trim();
    if (!message || isStreaming.value) return;

    ensureLens();
    const thisTurn = ++turn;
    messages.value.push({ role: 'user', content: message });
    messages.value.push({ role: 'assistant', content: '', error: null, result: null, turnId: null, capability: null, outcome: null });
    // The reactive proxy, so streamed updates re-render the transcript.
    current = messages.value[messages.value.length - 1];
    isStreaming.value = true;

    try {
        await lens.send(message, { page: currentPage() });
    } catch (error) {
        console.error('Lens panel request failed', { capabilityId: CAPABILITY_ID, conversationId: lens.conversationId.value, error });
        if (thisTurn === turn && current) current.error = { message: 'The AI service could not be reached. Please try again.', code: 'network_error' };
    }

    if (thisTurn !== turn) return;
    if (current) {
        if (lens.text.value) current.content = lens.text.value;
        if (lens.error.value && !current.error) current.error = { message: lens.error.value.message, code: lens.error.value.code };
    }
    isStreaming.value = false;
}

/**
 * The guides for a page (GET /api/help?route=…), fetched once per route for the session. Never
 * throws: a failure is logged and leaves no guides.
 */
async function loadPageGuides(routeName) {
    const key = String(routeName ?? '');
    if (!key) {
        pageGuides.value = [];
        return;
    }
    if (!guidesByRoute.has(key)) {
        try {
            const response = await $http.get('/api/help', { params: { route: key }, errorHandle: false });
            guidesByRoute.set(
                key,
                (response?.data?.areas ?? []).flatMap((area) => area.articles ?? []),
            );
        } catch (error) {
            console.error('Lens panel: page guides could not be loaded', { routeName: key, error });
            pageGuides.value = [];
            return;
        }
    }
    pageGuides.value = sample(guidesByRoute.get(key), PAGE_GUIDE_CHIPS);
}

/** Up to `count` items of `items` in random order (Fisher-Yates on a copy). */
function sample(items, count) {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, count);
}

/** Aborts the reply; what already streamed stays in the transcript. */
function stop() {
    turn++;
    lens?.stop();
    isStreaming.value = false;
}

/** A fresh conversation: stops any reply and forgets the server-side conversation. */
function startOver() {
    stop();
    current = null;
    messages.value = [];
    if (lens) lens.conversationId.value = null;
}

/**
 * Another account's conversation and guides never carry over: on an account switch the panel closes
 * and forgets its conversation and page guides. Guides are cleared for every route, since what a
 * reader may see differs by account.
 */
function watchAccount() {
    if (accountWatched) return;
    accountWatched = true;
    const authStore = useAuthStore();
    watch(
        () => authStore.account?.id,
        (accountId, previous) => {
            if (accountId === previous) return;
            startOver();
            close();
            pageGuides.value = [];
            guidesByRoute.clear();
        },
    );
}

function toggle() {
    open.value = !open.value;
}

function close() {
    open.value = false;
}

/**
 * The header Lens panel's state and chat over the `lens.chat` capability (#6435), the panel router
 * that hands each message to the capability that answers it (#6467). Every call returns the same
 * state.
 */
export function useLensPanel() {
    watchAccount();
    return { open, messages, isStreaming, pageGuides, send, loadPageGuides, stop, startOver, toggle, close };
}
